import { Link } from 'react-router-dom'
import { SourceCard as DSSourceCard, SourceCardList as DSSourceCardList } from '../../ds'
import type { PriorSourceCard, SourceCard as SourceCardData } from '../../services/api'
import ToolOutputRenderer from './ToolOutputRenderer'
import { STATUS_COPY, UNKNOWN_STATUS_COPY, allowedDeepLink, earlierReadLabel } from './sourceCardPolicy'

/**
 * One thing Marva read to answer (MAR-173 PR 3).
 *
 * The card is built by merchant-chat's CODE from a tool result
 * (`services/chat/merchant-chat/src/capabilities/registry.js` `toSourceCard`),
 * never parsed from model text. It says what was read, whether the read was
 * whole, and where in the app the merchant can see it.
 *
 * ⚠️ **A read that did not happen never looks like an empty success** (§3.4).
 * Only `ok` and `partial` render an output; every other status renders a short,
 * honest line instead, and nothing that could pass for "there is nothing".
 */

function statusLine(status: string): string | null {
  if (status === 'ok') return null
  return (STATUS_COPY as Record<string, string>)[status] ?? UNKNOWN_STATUS_COPY
}

interface SourceCardProps {
  card: SourceCardData
}

const SourceCard = ({ card }: SourceCardProps) => {
  const link = allowedDeepLink(card.deepLink)
  const read = card.status === 'ok' || card.status === 'partial'
  // The card's header already names it; an output title under it would repeat it.
  const output = read && card.output ? { ...card.output, title: undefined } : null

  return (
    <DSSourceCard
      title={card.title}
      status={card.status}
      statusText={statusLine(card.status) ?? undefined}
      recordId={card.recordId}
      link={link ? { label: link.label, to: link.to, as: Link } : undefined}
      dropped={read && !card.output && !!card.outputDropped}
    >
      {output && <ToolOutputRenderer outputs={[output]} />}
    </DSSourceCard>
  )
}

interface SourceCardListProps {
  cards: SourceCardData[]
}

/** Every card an answer carries, in the order the reads were made. */
export const SourceCardList = ({ cards }: SourceCardListProps) => {
  if (!cards || cards.length === 0) return null
  return (
    <DSSourceCardList>
      {cards.map((card, i) => (
        <SourceCard key={`${card.capability}-${card.recordId ?? ''}-${i}`} card={card} />
      ))}
    </DSSourceCardList>
  )
}

interface PriorSourceCardListProps {
  cards: PriorSourceCard[]
  now?: Date
}

/**
 * The earlier reads that were in front of Marva when she answered (MAR-173
 * replay) — context, not a claim they were used — labelled with when they were
 * read. Each card is the same `SourceCard` — so the deep-link allowlist and the
 * status copy apply unchanged — inside a `prior` list that says it is NOT this
 * answer's own read (dotted, no opacity: D-33).
 */
export const PriorSourceCardList = ({ cards, now }: PriorSourceCardListProps) => {
  if (!cards || cards.length === 0) return null
  const groups: { label: string; cards: PriorSourceCard[] }[] = []
  for (const card of cards) {
    const label = earlierReadLabel(card.readAt, now)
    const group = groups.find((g) => g.label === label)
    if (group) group.cards.push(card)
    else groups.push({ label, cards: [card] })
  }
  return (
    <DSSourceCardList prior>
      {groups.map((group) => (
        <DSSourceCardList key={group.label} label={group.label}>
          {group.cards.map((card, i) => (
            <SourceCard key={`${card.capability}-${card.recordId ?? ''}-${i}`} card={card} />
          ))}
        </DSSourceCardList>
      ))}
    </DSSourceCardList>
  )
}

export default SourceCard
